import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getAnonymousUserId } from '@wegic/common/anonymous';
import { AuthContainer } from '@wegic/login';
import { usePromotionStore } from '@wegic/price-ui';
import { Message, Modal } from '@wegic/ui';
import '@wegic/ui/dist/components/Message/index.css';
import '@wegic/ui/dist/components/Modal/index.css';
import { Fragment, useCallback, useEffect, useState } from 'react';
import CampaignCountdown from '@/components/CampaignCountdown';
import { GOOGLE_CLIENT_ID } from '@/const/env';
import { trackLogin, trackLoginClick, trackSignup } from '@/helpers/authTracking';
import { resumePaidLaunchOrderGenerationAfterLogin } from '@/helpers/launchOrderGeneration';
import { getCampaignEndTimestamp } from '@/helpers/loginCampaign';
import { consumePendingPayBeforeLoginAfterLogin } from '@/helpers/payBeforeLoginFlow';
import { handleUserLoginEffects, mapAuthUserToUserInfo } from '@/helpers/user';
import { defaultLanguage } from '@/i18n/const';
import { getLanguageSetting } from '@/i18n/helper';
import { getUserAPIUrl } from '@/services/request/helper';
import { useI18nStore } from '@/stores/i18n';
import { useLoginModalStore } from '@/stores/loginModal';
import { useUserStore } from '@/stores/user';
import styles from './index.module.less';
const CAMPAIGN_IMAGE_URL = 'https://cdn.wegic.ai/wegic-asset-store/img/aug-promo-login-ip.png';
const MOBILE_CAMPAIGN_IMAGE_URL = 'https://cdn.wegic.ai/wegic-asset-store/img/aug-promo-login-modal-ip.png';
const CAMPAIGN_BACKGROUND_COLOR = '#016AFD';
const DESKTOP_BREAKPOINT = 1024;
const CAMPAIGN_LOGIN_STYLE = {
    '--wg-login-content-padding': '1px',
    '--wg-login-content-padding-mobile': '1px',
    '--wg-login-google-button-height': '60px',
    '--wg-login-divider-margin': '8px 0',
    '--wg-login-forget-password-margin-bottom': '5px',
    '--wg-login-policy-margin-top': '11px'
};
function renderCampaignSubtitle(text, couponClassName) {
    const parts = text.split(/(<(?:highlight|coupon)>.*?<\/(?:highlight|coupon)>)/g);
    return parts.map((part, index)=>{
        const match = part.match(/^<(highlight|coupon)>(.*?)<\/\1>$/);
        if (!match) {
            return /*#__PURE__*/ _jsx(Fragment, {
                children: part
            }, index);
        }
        return match[1] === 'coupon' ? /*#__PURE__*/ _jsx("span", {
            className: couponClassName,
            children: match[2]
        }, index) : /*#__PURE__*/ _jsx(Fragment, {
            children: match[2]
        }, index);
    });
}
function DesktopCampaignTitle() {
    const title = intl.get('login.campaign.title').replace(/([：:])\s*/, '$1\n');
    return /*#__PURE__*/ _jsxs("div", {
        className: styles['desktop-title'],
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: styles['desktop-title-text'],
                children: title
            }),
            /*#__PURE__*/ _jsx("p", {
                className: styles['desktop-subtitle'],
                children: renderCampaignSubtitle(intl.get('login.campaign.subtitle'), styles['desktop-coupon'])
            })
        ]
    });
}
function MobileCampaignTitle() {
    const title = intl.get('login.campaign.title').replace(/([：:])\s*/, '$1\n');
    return /*#__PURE__*/ _jsxs("div", {
        className: styles['mobile-title'],
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: styles['mobile-title-text'],
                children: title
            }),
            /*#__PURE__*/ _jsx("p", {
                className: styles['mobile-subtitle'],
                children: renderCampaignSubtitle(intl.get('login.campaign.subtitle'), styles['mobile-coupon'])
            })
        ]
    });
}
function CloseIcon(param) {
    let { size } = param;
    return /*#__PURE__*/ _jsx("svg", {
        width: size,
        height: size,
        viewBox: "0 0 16 16",
        fill: "none",
        "aria-hidden": "true",
        children: /*#__PURE__*/ _jsx("path", {
            d: "M3 3L13 13M13 3L3 13",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round"
        })
    });
}
function preventEscapeDismiss(event) {
    event.preventDefault();
}
export default function LoginModal() {
    const visible = useLoginModalStore((state)=>state.visible);
    const close = useLoginModalStore((state)=>state.close);
    const onSuccess = useLoginModalStore((state)=>state.onSuccess);
    const initialEmail = useLoginModalStore((state)=>state.initialEmail);
    const modalHeader = useLoginModalStore((state)=>state.header);
    const dismissible = useLoginModalStore((state)=>state.dismissible);
    const storedLanguage = useI18nStore((state)=>state.currentLanguage);
    const currentLanguage = getLanguageSetting() || storedLanguage || defaultLanguage;
    const setRegisterInfo = useUserStore((state)=>state.setRegisterInfo);
    const discountActivity = usePromotionStore((state)=>state.discountActivity);
    const campaignEndTimestamp = getCampaignEndTimestamp(discountActivity);
    const shouldShowCampaign = campaignEndTimestamp !== null;
    const onCancel = dismissible ? close : undefined;
    const dialogContentProps = dismissible ? undefined : {
        onEscapeKeyDown: preventEscapeDismiss
    };
    const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth > DESKTOP_BREAKPOINT : true);
    useEffect(()=>{
        const handleResize = ()=>setIsDesktop(window.innerWidth > DESKTOP_BREAKPOINT);
        window.addEventListener('resize', handleResize);
        return ()=>window.removeEventListener('resize', handleResize);
    }, []);
    const handleLoginSuccess = useCallback(async (userInfo, afterLogin)=>{
        try {
            await handleUserLoginEffects(userInfo);
            afterLogin === null || afterLogin === void 0 ? void 0 : afterLogin(userInfo);
            close();
            const handledPendingPayment = await consumePendingPayBeforeLoginAfterLogin({
                resume: resumePaidLaunchOrderGenerationAfterLogin
            });
            if (!handledPendingPayment) {
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
            }
        } catch  {
            Message.error(intl.get('Login_Failed_Message'));
        }
    }, [
        close,
        onSuccess
    ]);
    const baseConfig = {
        initialEmail: initialEmail ?? undefined,
        api: {
            baseURL: getUserAPIUrl(),
            googleClientId: GOOGLE_CLIENT_ID
        },
        getAnonymousId: getAnonymousUserId,
        ui: {
            language: currentLanguage,
            header: modalHeader ?? (!shouldShowCampaign || isDesktop ? {
                title: intl.get('projects.guest.modal.title')
            } : undefined)
        },
        onEvent: async (event)=>{
            switch(event.type){
                case 'login:click':
                    trackLoginClick(event.loginType);
                    break;
                case 'login:email:success':
                    {
                        const userInfo = mapAuthUserToUserInfo(event.data);
                        await handleLoginSuccess(userInfo, ()=>trackLogin(userInfo.userId, 'email'));
                        break;
                    }
                case 'login:google:success':
                    {
                        const userInfo = mapAuthUserToUserInfo(event.data);
                        await handleLoginSuccess(userInfo, ()=>{
                            if (event.isFirstRegister) {
                                trackSignup(userInfo, 'google');
                            } else {
                                trackLogin(userInfo.userId, 'google');
                            }
                        });
                        break;
                    }
                case 'password:reset:success':
                    {
                        const userInfo = mapAuthUserToUserInfo(event.data);
                        await handleLoginSuccess(userInfo, ()=>trackLogin(userInfo.userId, 'password_reset'));
                        break;
                    }
                case 'register:start':
                    setRegisterInfo(event.formData);
                    break;
                case 'register:success':
                    {
                        const userInfo = mapAuthUserToUserInfo(event.data);
                        await handleLoginSuccess(userInfo, ()=>trackSignup(userInfo, 'email'));
                        break;
                    }
            }
        }
    };
    if (!shouldShowCampaign) {
        return /*#__PURE__*/ _jsx(Modal, {
            visible: visible,
            onCancel: onCancel,
            hideFooter: true,
            unmountOnExit: true,
            maskIsClose: false,
            closable: dismissible,
            dialogContentProps: dialogContentProps,
            className: styles['plain-modal'],
            children: /*#__PURE__*/ _jsx(AuthContainer, {
                config: baseConfig,
                className: styles['auth-container']
            })
        });
    }
    return /*#__PURE__*/ _jsx(Modal, {
        visible: visible,
        onCancel: onCancel,
        hideFooter: true,
        unmountOnExit: true,
        maskIsClose: false,
        closable: false,
        dialogContentProps: dialogContentProps,
        className: isDesktop ? styles['login-modal'] : styles['login-modal-mobile'],
        children: isDesktop ? /*#__PURE__*/ _jsxs("div", {
            className: styles['desktop-content'],
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles['auth-wrap'],
                    style: CAMPAIGN_LOGIN_STYLE,
                    children: /*#__PURE__*/ _jsx(AuthContainer, {
                        config: baseConfig
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles['campaign-side'],
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles['campaign-canvas'],
                        style: {
                            backgroundColor: CAMPAIGN_BACKGROUND_COLOR
                        },
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles['campaign-white-panel'],
                                "aria-hidden": "true"
                            }),
                            /*#__PURE__*/ _jsx(DesktopCampaignTitle, {}),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['campaign-media'],
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: styles['campaign-visuals'],
                                        children: [
                                            /*#__PURE__*/ _jsx("img", {
                                                src: CAMPAIGN_IMAGE_URL,
                                                alt: "",
                                                "aria-hidden": "true",
                                                className: styles['campaign-img']
                                            }),
                                            /*#__PURE__*/ _jsx(CampaignCountdown, {
                                                targetTimestamp: campaignEndTimestamp,
                                                labelColor: "#000000",
                                                separatorColor: "#FF5410",
                                                className: styles['campaign-countdown']
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: styles['campaign-tagline'],
                                        children: intl.get('login.campaign.tagline')
                                    })
                                ]
                            }),
                            dismissible && /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: close,
                                className: styles['desktop-close-btn'],
                                "aria-label": "Close",
                                children: /*#__PURE__*/ _jsx(CloseIcon, {
                                    size: 16
                                })
                            })
                        ]
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsxs("div", {
            className: styles['mobile-content'],
            children: [
                /*#__PURE__*/ _jsx("img", {
                    src: MOBILE_CAMPAIGN_IMAGE_URL,
                    alt: "",
                    "aria-hidden": "true",
                    className: styles['mobile-banner']
                }),
                dismissible && /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    onClick: close,
                    className: styles['mobile-close-btn'],
                    "aria-label": "Close",
                    children: /*#__PURE__*/ _jsx(CloseIcon, {
                        size: 14
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles['mobile-card'],
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles['mobile-inner'],
                        children: [
                            /*#__PURE__*/ _jsx(MobileCampaignTitle, {}),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles['mobile-auth'],
                                style: CAMPAIGN_LOGIN_STYLE,
                                children: /*#__PURE__*/ _jsx(AuthContainer, {
                                    config: baseConfig
                                })
                            })
                        ]
                    })
                })
            ]
        })
    });
}
